// client/src/components/onboarding/VoiceVerification.tsx
import React, { useState, useRef, useEffect } from "react";
import "../../assets/styles/VoiceVerification.css";
import BiometricService from "../../services/biometricService";

interface VoiceVerificationProps {
  onVerified: () => void;
  userId: string;
  studentName?: string;
  schoolName?: string;
}

const VoiceVerification: React.FC<VoiceVerificationProps> = ({ 
  onVerified, 
  userId,
  studentName = "Mary Atieno", 
  schoolName = "Makini School" 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const biometricService = BiometricService.getInstance();

  useEffect(() => {
    return () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          try {
            const voiceData = await biometricService.captureVoiceData(stream);
            await biometricService.storeBiometricData(userId, undefined, voiceData);
            setIsVerified(true);
          } catch (err) {
            setError("Failed to process voice data. Please try again.");
            console.error("Voice processing error:", err);
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Stop recording after 5 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
        }
      }, 5000);
    } catch (err) {
      setError("Failed to access microphone. Please ensure microphone permissions are granted.");
      console.error("Microphone access error:", err);
    }
  };

  return (
    <div className="voice-verification-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          <span>L</span>
        </div>
        <div className="profile-name">LavernAI</div>
      </div>

      {/* Voice Verification Content */}
      <div className="verification-content">
        <div className="microphone-icon-container" onClick={!isRecording ? startRecording : undefined}>
          <div className={`microphone-icon ${isRecording ? 'recording' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 16c2.206 0 4-1.794 4-4V6c0-2.206-1.794-4-4-4S8 3.794 8 6v6c0 2.206 1.794 4 4 4zm0-14c1.103 0 2 .897 2 2v6c0 1.103-.897 2-2 2s-2-.897-2-2V4c0-1.103.897-2 2-2z"/>
              <path d="M19 12v-2a1 1 0 0 0-2 0v2c0 2.757-2.243 5-5 5s-5-2.243-5-5v-2a1 1 0 0 0-2 0v2c0 3.52 2.613 6.432 6 6.92V20H8a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2h-3v-1.08c3.387-.488 6-3.4 6-6.92z"/>
            </svg>
          </div>
        </div>

        <h1 className="verification-title">Voice Verification</h1>
        
        {error && <div className="error-message">{error}</div>}
        
        {!isRecording && !isVerified && (
          <div className="verification-instructions">
            Kindly speak out your full name and the name of your school.
            <p className="small-text">
              This helps us securely verify your identity during live lessons to prevent impersonation.
            </p>
            <button className="record-button" onClick={startRecording}>
              Tap to begin
            </button>
          </div>
        )}
        
        {isRecording && (
          <div className="recording-container">
            <div className="recording-pulse"></div>
            <p>Recording in progress...</p>
            <p className="prompt-text">Please say: "My name is {studentName} from {schoolName}"</p>
          </div>
        )}
        
        {isVerified && (
          <div className="verification-status">
            <div className="status-indicator"></div>
            <div className="status-text">Voice pattern captured</div>
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="button-container">
        <button 
          className={`continue-button ${isVerified ? 'active' : 'disabled'}`} 
          onClick={isVerified ? onVerified : undefined}
          disabled={!isVerified}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default VoiceVerification;